import moment from "moment"
import React, { useEffect, useState } from "react"
import { FormattedMessage, injectIntl } from "react-intl"
import { useDispatch } from "react-redux"
import { displayAlert } from "../../../../state/alert/reducer"
import { isError } from "../../../../state/helpers/Error"
import { InvoiceStatBox } from "../../../../state/listing/generic-listing/column-definitions/invoices"
import { StatBoxFilters } from "../../../../state/listing/generic-listing/index"
import SearchService from "../../../../state/search/SearchService"
import SearchTotals from "../../../../state/search/SearchTotals"
import InvoiceStats, { InvoiceStatsAttributes } from "../../../../state/stats/InvoiceStats"
import StatsService from "../../../../state/stats/StatsService"
import { getFormattedDate, getLastMonth, getYesterday } from "../../../../utils/date/utils"
import { AlertVariant } from "../../../alert/Alert"
import StatBox from "../../statbox"

interface OwnProps {
    selected: string,
    onChange: (
        statBox: string,
        filters: StatBoxFilters,
        previousFilters: StatBoxFilters,
    ) => void,
}

const InvoicesStatBoxes = (props: OwnProps) => {
    const dispatch = useDispatch()
    const [overdueAmount, setOverdueAmount] = useState<string>("0")
    const [overdueCount, setOverdueCount] = useState<number>(0)
    const [fullyPaidAmount, setFullyPaidAmount] = useState<string>("0")
    const [fullyPaidCount, setFullyPaidCount] = useState<number>(0)
    const [stats, setStats] = useState<InvoiceStatsAttributes>()

    useEffect(() => {
        const fetchData = async () => {
            await Promise.all([
                getSearchTotalsOverdue(),
                getSearchTotalsTotallyPaid(),
                getStats(),
            ])
        }

        fetchData()
    }, [])

    const getStats = async () => {
        const stats = await StatsService.GetInvoiceStats()

        if (isError(stats)) {
            dispatch(displayAlert({ type: AlertVariant.DANGER, message: "Error: Could not load invoice stats" }))
        } else {
            setStats((stats as InvoiceStats).getAttributes())
        }
    }

    const getSearchTotalsOverdue = async () => {
        const postData = {
            keywords: "*",
            objects: ["invoices"],
            fields: [
                "identifier",
            ],
            totals: [
                "baseCurrencyBalance",
            ],
            filters: {
                billingType: "one-time",
                status: ["sent", "partially-paid"],
                dueDate: "<;" + getFormattedDate(moment().startOf("day"))
            },
        }

        const res = await SearchService.mainSearchTotals(postData)

        if (isError(res)) {
            dispatch(displayAlert({ type: AlertVariant.DANGER, message: "Error: Could not load search totals" }))
        } else {
            setOverdueAmount((res as SearchTotals).getAttributes().totals.baseCurrencyBalance)
            setOverdueCount((res as SearchTotals).getAttributes().totalItemsCount)
        }
    }

    const getSearchTotalsTotallyPaid = async () => {
        const postData = {
            keywords: "*",
            objects: ["invoices"],
            fields: [
                "identifier",
            ],
            totals: [
                "baseCurrencyTotalAmount",
            ],
            filters: {
                billingType: "one-time",
                status: ["fully-paid"],
                effectiveDate: ">;" + getFormattedDate(moment().endOf("day").subtract(30, "days")),
            },
        }

        const res = await SearchService.mainSearchTotals(postData)

        if (isError(res)) {
            dispatch(displayAlert({ type: AlertVariant.DANGER, message: "Error: Could not load search totals" }))
        } else {
            setFullyPaidAmount((res as SearchTotals).getAttributes().totals.baseCurrencyTotalAmount)
            setFullyPaidCount((res as SearchTotals).getAttributes().totalItemsCount)
        }
    }

    const getFilterForStatBox = (id: string): StatBoxFilters => {
        switch (id) {
        case InvoiceStatBox.OVERDUE:
            return {
                "statuses": {
                    nameId: "statuses",
                    value: ["sent", "partially-paid"],
                    op: "isOneOf",
                    label: "Waiting for Payment, Partially paid"
                },
                "dueDate":  {
                    nameId: "dueDate",
                    value: { startDate: getYesterday() },
                    op: "lowerThan",
                    label: `${moment(getYesterday()).format("MMMM Do YYYY")}`
                },
            }
        case InvoiceStatBox.OPEN:
            return {
                "statuses": {
                    nameId: "statuses",
                    value: ["sent", "partially-paid"],
                    op: "isOneOf",
                    label: "Waiting for Payment, Partially paid"
                }
            }
        case InvoiceStatBox.FULLY_PAID_30_DAYS:
            return {
                "statuses": {
                    nameId: "statuses",
                    value: ["fully-paid"],
                    op: "isOneOf",
                    label: "Fully paid"
                },
                "effectiveDate":  {
                    nameId: "effectiveDate",
                    value: { startDate: getLastMonth() },
                    op: "greaterThan",
                    label: `${moment(getLastMonth()).format("MMMM Do YYYY")}`
                },
            }
        case InvoiceStatBox.DRAFT:
            return {
                "statuses": {
                    nameId: "statuses",
                    value: ["draft"],
                    op: "isOneOf",
                    label: "Draft"
                }
            }
        default:
            throw new Error("Error: Invalid stat box type for filters")
        }
    }

    const handleChange = (statBoxId: string) => {
        props.onChange(
            statBoxId,
            statBoxId ? getFilterForStatBox(statBoxId) : {},
            props.selected ? getFilterForStatBox(props.selected) : {},
        )
    }

    return (
        <>
            <StatBox
                id={InvoiceStatBox.OVERDUE}
                className="danger-box"
                activeClassName="active-danger-box"
                selected={props.selected === InvoiceStatBox.OVERDUE}
                count={overdueCount}
                amount={overdueAmount}
                label={<FormattedMessage
                    id="invoice.stat-box.overdue"
                    defaultMessage="Overdue"
                />}
                onChange={handleChange}
            />
            <StatBox
                id={InvoiceStatBox.DRAFT}
                selected={props.selected === InvoiceStatBox.DRAFT}
                count={stats?.draftCount ?? 0}
                amount={stats?.draftAmount ?? "0"}
                label={<FormattedMessage
                    id="invoice.stat-box.draft"
                    defaultMessage="Draft"
                />}
                onChange={handleChange}
            />
            <StatBox
                id={InvoiceStatBox.OPEN}
                selected={props.selected === InvoiceStatBox.OPEN}
                count={(stats?.partiallyPaidCount ?? 0) + (stats?.sentCount ?? 0)}
                amount={(parseFloat(stats?.partiallyPaidAmount ?? "0") + parseFloat(stats?.sentAmount ?? "0"))}
                label={<FormattedMessage
                    id="invoice.stat-box.open_invoices"
                    defaultMessage="Open invoices"
                />}
                onChange={handleChange}
            />
            <StatBox
                id={InvoiceStatBox.FULLY_PAID_30_DAYS}
                className="success-box"
                activeClassName="active-success-box"
                selected={props.selected === InvoiceStatBox.FULLY_PAID_30_DAYS}
                count={fullyPaidCount}
                amount={fullyPaidAmount}
                label={<FormattedMessage
                    id="quote.stat-box.fully_paid_last_30_days"
                    defaultMessage="Fully paid last 30 days"
                />}
                onChange={handleChange}
            />
        </>
    )
}

export default injectIntl(InvoicesStatBoxes)


// WEBPACK FOOTER //
// ./src/common/listing/generic-listing/stat-boxes/InvoicesStatBoxes.tsx